'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Position, PRODUCT_NOTIFY_ME } from '@headless-workspace/config';
import { CONTENT_ASSET_KEYS } from '@headless-workspace/core-domain';
import { SideMenuModal, useToasts, useUIContext } from '@headless-workspace/core-ui';
import {
    CtaType,
    NotifyMeModal,
    ProductDetailInfoCTA,
    ProductDetailInfoModal,
    ProductDetailInfoModalContext,
    StoresDeliveryContainer,
    useContentAssets,
    useProductDeliveryBusinessRules,
    useProductVariantDetailContext,
} from '@headless-workspace/domain/common/client';
import { HTMLString } from '@headless-workspace/domain/common/server';
import { GlowButton, GlowDivider, Icons, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { DeliveryInformation } from './DeliveryInformation';
import { ProductDetailDeliveryInfo } from './ProductDetailDeliveryInfo';

type ProductDetailDeliveryZoneContainerProps = PropsWithLocale;

export const ProductDetailDeliveryZoneContainer: Component<ProductDetailDeliveryZoneContainerProps> = ({ locale }) => {
    const discoverTranslations = useTranslations('Discover.Pdp.deliveryZone');
    const commonTranslations = useTranslations('Modal.stores');
    const { notifyError } = useToasts();
    const deliveryInformation = useProductDeliveryBusinessRules();
    const {
        productVariantDetails: { sku },
    } = useProductVariantDetailContext();

    const {
        menuModal: { addIfAbsentAndOpen },
    } = useUIContext();

    const openNotifyMeModal = useCallback(() => {
        addIfAbsentAndOpen(
            PRODUCT_NOTIFY_ME,
            SideMenuModal(Position.Right, {
                default: <NotifyMeModal locale={locale} productIds={[sku]} />,
            }),
        );
    }, [addIfAbsentAndOpen, locale, sku]);

    const renderNotifyMeCTA = useCallback(
        (label: string) => (
            <GlowButton label={label} onClick={openNotifyMeModal} Icon={Icons.Notification} variant={'secondary'} />
        ),
        [openNotifyMeModal],
    );

    const { isLoading: isCartDeliveryLoading, result: cartDeliveryPopupResult } = useContentAssets([
        CONTENT_ASSET_KEYS.CART_DELIVERY_POPUP,
    ]);

    const [title, setTitle] = useState<string>('');
    const [htmlContent, setHtmlContent] = useState<HTMLString>(HTMLString(''));

    useEffect(() => {
        if (!isCartDeliveryLoading) {
            switch (cartDeliveryPopupResult.type) {
                case 'success': {
                    const asset = cartDeliveryPopupResult.data.find(
                        (asset) => asset.id === CONTENT_ASSET_KEYS.CART_DELIVERY_POPUP,
                    )?.content;
                    setTitle(asset?.title ?? '');
                    setHtmlContent(asset?.html ?? HTMLString(''));
                    break;
                }
                case 'failure': {
                    notifyError(discoverTranslations('errors.informationFetchError'));
                    break;
                }
                case 'empty': {
                    notifyError(discoverTranslations('errors.informationEmpty'));
                    break;
                }
            }
        }
    }, [cartDeliveryPopupResult, isCartDeliveryLoading, notifyError, discoverTranslations]);

    const warehouseCTA = useMemo(() => {
        if (deliveryInformation.isLoading) {
            return undefined;
        }

        if (deliveryInformation.warehouse.cta?.type === CtaType.NotifyMe) {
            return renderNotifyMeCTA(deliveryInformation.warehouse.cta.label);
        }

        return undefined;
    }, [deliveryInformation, renderNotifyMeCTA]);

    const selectStoreButtonElement = useCallback(
        (cta: { label: string; type: CtaType }, onClick: () => void) => (
            <GlowButton label={cta.label} onClick={onClick} variant={'secondary'} />
        ),
        [],
    );

    const storeCTA = useMemo(() => {
        if (deliveryInformation.isLoading) {
            return undefined;
        }

        const { cta } = deliveryInformation.store;

        switch (cta?.type) {
            case CtaType.SelectStore:
            case CtaType.ChangeStore: {
                return (
                    <ProductDetailInfoCTA
                        menu={
                            <ProductDetailInfoModal
                                title={commonTranslations('label.storeAvailability')}
                                renderInformation={<StoresDeliveryContainer locale={locale} />}
                                context={ProductDetailInfoModalContext.storeSelection}
                                isSidePanelDeliveryZone={true}
                            />
                        }
                        buttonElement={(onClick) => selectStoreButtonElement(cta, onClick)}
                    />
                );
            }
            case CtaType.NotifyMe:
                return renderNotifyMeCTA(cta.label);
            default:
                return undefined;
        }
    }, [commonTranslations, deliveryInformation, locale, renderNotifyMeCTA, selectStoreButtonElement]);

    if (deliveryInformation.isLoading) {
        return (
            <div
                id={'product-delivery-zone'}
                className={'flex flex-col gap-1.5 border-medium border-solid border-border-primary rounded-0.5 py-1.5'}
            >
                <DeliveryInformation isLoading />
                <GlowDivider />
                <DeliveryInformation isLoading />
            </div>
        );
    }

    return (
        <div
            id={'product-delivery-zone'}
            className={'flex flex-col gap-1.5 border-medium border-solid border-border-primary rounded-0.5 py-1.5'}
        >
            <DeliveryInformation
                isLoading={deliveryInformation.isLoading}
                Icon={Icons.Truck}
                title={deliveryInformation.warehouse?.title}
                description={deliveryInformation.warehouse?.description}
                status={deliveryInformation.warehouse?.status}
                cta={warehouseCTA}
            />
            <GlowDivider />
            <DeliveryInformation
                isLoading={deliveryInformation.isLoading}
                Icon={Icons.Storefront}
                title={deliveryInformation.store.title}
                description={deliveryInformation.store.description}
                status={deliveryInformation.store.status}
                cta={storeCTA}
            />
            {deliveryInformation.termsAndConditions && (
                <>
                    <GlowDivider />
                    <ProductDetailInfoCTA
                        menu={
                            <ProductDetailInfoModal
                                title={title}
                                closeButtonLabel={deliveryInformation.termsAndConditions.ctaLabel}
                                renderInformation={<ProductDetailDeliveryInfo deliveryInfo={htmlContent} />}
                                contentContainerClassName={
                                    'p-1.5 gap-1 overflow-x-auto h-productInfoReturnModalInfoContainer'
                                }
                            />
                        }
                        labelElement={discoverTranslations('action.deliveryAndReturns')}
                        className={'px-1.5 text-text-primary'}
                    />
                </>
            )}
        </div>
    );
};
